"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createNewlyAssignedExtendedResponseTape = exports.createExtendedResponseTape = void 0;
const lodash_1 = __importDefault(require("lodash"));
const remeda_1 = require("remeda");
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const progressHelper_1 = require("./progressHelper");
const RequiredIntermediateFields_1 = require("./RequiredIntermediateFields");
// @required-fields
// valley bank wants advanceRatePath in their extended response tape, need to hardcode these for now
const env = common_1.utils.EnvUtils.getEnv();
const valleyBankLoanProductIds = {
    // same as stage, assuming this was dumped.
    development: [
        '40941652-b01f-400e-a215-0bf7459ab3bf',
        '15618a52-8f23-4284-8b3e-fbd955c41079', // securities
    ],
    stage: [
        '40941652-b01f-400e-a215-0bf7459ab3bf',
        '15618a52-8f23-4284-8b3e-fbd955c41079', // securities
    ],
    prod: [
        '1cde1a2f-d558-4f01-86fc-c48178fd1105',
        'a12c120c-e7ff-40e1-a315-9d70f769714d', // securities
    ],
};
const getValleyBankLoanProductIds = () => valleyBankLoanProductIds[env];
// @required-fields
// eventually we want to allow these into extended response tape,
// we need to carefully manage template rollover
const EXCLUDED_BORROWING_BASE_FIELD_NAMES = [
    'advanceRatePath',
];
// @required-fields
// eventually we want to allow these into extended response tape,
// we need to carefully manage template rollover
const EXCLUDED_REQUIRED_INTERMEDIATE_FIELD_NAMES = [
    'normalizedDaysPastDue',
    'normalizedDurationDays',
    'normalizedisChargedOff',
    'normalizedisDelinquent',
    'normalizedOriginationDate',
    'normalizedInitialBalance',
];
/**
 * Single place to create the extendedResponseTape data structure.
 * NOTE: The exact order of applying fields to the extendedResponseTape object is intentional, and any updates should be made with caution.
 * The output of this gets applied to our Servicer Report XLSX, so we have kept this less DRY on purpose, to ensure predictability and consistency
 * with what to expect in both the CSV and XLSX outputs.
 */
const createExtendedResponseTape = ({ multiLoanProductConcentrationLimits, loanProductMechanics, borrowingBaseReceivables: allBorrowingBaseReceivables, progressCallback, }) => __awaiter(void 0, void 0, void 0, function* () {
    const { loanProductId } = loanProductMechanics;
    const borrowingBaseReceivables = (0, remeda_1.filter)(allBorrowingBaseReceivables, (receivable) => receivable.loanProductId === loanProductMechanics.loanProductId);
    const singleLPConcentrationLimitKeys = lodash_1.default.map(loanProductMechanics.concentrationLimitConfigs, ({ propertyKey }) => propertyKey);
    const multiLPConcentrationLimitKeys = multiLoanProductConcentrationLimits.map(({ propertyKey }) => propertyKey);
    const concentrationLimitKeys = [
        ...singleLPConcentrationLimitKeys,
        ...multiLPConcentrationLimitKeys,
    ];
    const { updateProgress } = (0, progressHelper_1.createProgressHelper)({
        progressCallback,
        operation: 'Create extended response tape',
        numRows: borrowingBaseReceivables.length,
    });
    // HACK ALERT - to support valley bank and not disrupt other customers
    const vbLoanProductIds = getValleyBankLoanProductIds();
    const mappedLoanTapeWithCalcs = lodash_1.default.map(borrowingBaseReceivables, (receivable, index) => __awaiter(void 0, void 0, void 0, function* () {
        yield updateProgress(index);
        // Type cast to BorrowingBaseReceivable to catch any issues when the
        // underlying type changes.
        // There is still a risk that the BorrowingBaseReceivable type changes and
        // we are reading older JSON. Perhaps the JSON need to have version field?
        // const receivableJson = calcRow.receivableJson as ReceivableTypes.CreditFacility.BorrowingBaseReceivable;
        const extendedResponseTape = {
            externalReceivableId: receivable.externalReceivableId,
            finleyReceivableId: receivable.finleyReceivableId,
        };
        const filteredBaseFields = lodash_1.default.omit(receivable.baseFields, [
            'finley_sanitized',
        ]);
        // output specific order based on the config, now that we allow reordering of fields
        lodash_1.default.forEach(loanProductMechanics._config_.baseFieldConfigs, (baseFieldConfig) => {
            const baseFieldName = `baseFields_${baseFieldConfig.field}`;
            extendedResponseTape[baseFieldName] =
                filteredBaseFields[baseFieldConfig.field];
        });
        // intermediate fields
        lodash_1.default.forEach(loanProductMechanics._config_.intermediateCalcConfigs, (intermediateCalcConfig) => {
            const fieldName = intermediateCalcConfig.field;
            if ((0, RequiredIntermediateFields_1.isRequiredIntermediateFieldName)(fieldName)) {
                // @required-fields ignore excluded required intermediate fields
                if (!lodash_1.default.includes(EXCLUDED_REQUIRED_INTERMEDIATE_FIELD_NAMES, fieldName)) {
                    extendedResponseTape[`requiredIntermediateFields_${fieldName}`] =
                        receivable.requiredIntermediateFields[fieldName];
                }
            }
            else {
                extendedResponseTape[`dynamicIntermediateFields_${fieldName}`] =
                    receivable.dynamicIntermediateFields[fieldName];
            }
        });
        // post processed fields
        lodash_1.default.forEach(receivable.postProcessedFields, (value, key) => {
            if (key === 'normalizedExposure') {
                extendedResponseTape[`postProcessedFields_${key}`] = value;
            }
        });
        // valuation
        lodash_1.default.forEach(receivable.valuationFields, (value, key) => {
            extendedResponseTape[`valuationFields_${key}`] = value;
        });
        // one column per eligibility criteria
        lodash_1.default.forEach(receivable.eligibilityStatuses, ({ eligibilityStatus, eligibilityCriteriaKey }) => {
            extendedResponseTape[`eligibilityStatus_${eligibilityCriteriaKey}`] = eligibilityStatus;
        });
        lodash_1.default.forEach(receivable.eligibilityFields, (value, key) => {
            extendedResponseTape[`eligibilityFields_${key}`] = value;
        });
        // concentration
        // one column per concentration limit
        // looks for multiLP concentrations as well.
        let totalConcentrationReduction = 0;
        lodash_1.default.forEach(concentrationLimitKeys, (propertyKey) => {
            const breachedConcentration = lodash_1.default.find(receivable.concentrationLimitReasons, (concentrationLimitReason) => concentrationLimitReason.propertyKey === propertyKey);
            const concentrationKickout = breachedConcentration
                ? breachedConcentration.concentrationLimitKickout
                : 0;
            totalConcentrationReduction += concentrationKickout;
            extendedResponseTape[`concentrationLimitFields_${propertyKey}`] = concentrationKickout;
        });
        // concentration
        extendedResponseTape.concentrationLimitFields_totalConcentrationReduction = totalConcentrationReduction;
        lodash_1.default.forEach(receivable.concentrationLimitFields, (value, key) => {
            extendedResponseTape[`concentrationLimitFields_${key}`] = value;
        });
        lodash_1.default.forEach(receivable.borrowingBaseFields, (value, key) => {
            // IF NOT(EXCLUDED FIELD) OR VALLEY BANK
            if (!lodash_1.default.includes(EXCLUDED_BORROWING_BASE_FIELD_NAMES, key) ||
                vbLoanProductIds.includes(loanProductId)) {
                extendedResponseTape[`borrowingBaseFields_${key}`] = value;
            }
        });
        extendedResponseTape.assignmentFields_assignedAt = receivable.assignmentFields
            ? receivable.assignmentFields.assignedAt
            : null;
        extendedResponseTape.assignmentFields_receivableStatus = receivable.assignmentFields
            ? finley_types_1.Receivable.ReceivableStatus.Assigned
            : null;
        return Object.assign({}, extendedResponseTape);
    }));
    return Promise.all(mappedLoanTapeWithCalcs);
});
exports.createExtendedResponseTape = createExtendedResponseTape;
/**
 * Filters an existing extended response tape
 * @returns an extended response tape with only the receivables
 * that were assigned with the specified reportingDate and fileVersion
 */
const createNewlyAssignedExtendedResponseTape = ({ extendedResponseTape, creditFacilityId, reportingDate, fileVersion, receivableResponseTape, creditFacilitySlug, }) => {
    const receivableMap = lodash_1.default.keyBy(receivableResponseTape.receivables, ({ finleyReceivableId }) => finleyReceivableId);
    const metadata = {
        logMeta: {
            creditFacilityId,
            creditFacilitySlug,
            reportingDate,
            fileVersion,
        },
    };
    return lodash_1.default.filter(extendedResponseTape, (responseTapeRow) => {
        const finleyReceivableId = responseTapeRow.finleyReceivableId;
        (0, common_1.finleyAssert)(lodash_1.default.isString(finleyReceivableId), new common_1.error.FinleyError(`finleyReceivableId ${finleyReceivableId} from extended response tape must be a string`, {
            metadata,
        }));
        const receivable = receivableMap[finleyReceivableId];
        (0, common_1.finleyAssert)(receivable && receivable.assignmentFields, new common_1.error.FinleyError(`assignmentFields must exist for finleyReceivableId ${finleyReceivableId}`, {
            metadata,
        }));
        return (receivable.assignmentFields.creditFacilityId === creditFacilityId &&
            receivable.assignmentFields.fileVersion === fileVersion &&
            receivable.assignmentFields.reportingDate === reportingDate);
    });
};
exports.createNewlyAssignedExtendedResponseTape = createNewlyAssignedExtendedResponseTape;
//# sourceMappingURL=extendedResponseTape.js.map